import { uniq } from 'lodash/fp';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { gql, useQuery } from '@apollo/client';
import { Box, Grid, Typography } from '../../../../1-primative';
import { UserContext } from '../../../../../providers/userContextProvider';
import { TranslatedString } from '../../../../../interfaces';
import { translateBackend } from '../../../../../assets/i18n/config';
import {
  Button,
  Card, CardContent, Switch, TextField,
} from '../../../../2-component';
import { OrganizationUserSelect } from '../../../../3-pattern';
import { WorkflowContext } from '../../workflowCompletion';

export interface FormSignerMapping {
  signerKey: string,
  signerName: TranslatedString,
  firstName?: string,
  lastName?: string,
  email?: string,
  organizationUserId?: string,
  useOrgUser?: boolean,
}

const FETCH_SIGNER_TYPES = gql`
  query fetchSignerTypes($input: FetchSignerTypesInput!) {
    fetchSignerTypes(input: $input) {
      signerTypes {
        id
        key
        translatedName { en fr }
        isPrimary
      }
      totalCount
    }
  }
`;

const FETCH_USER = gql`
  query fetchUser($userId: ObjectID!) {
    fetchUser(userId: $userId) {
      user {
        id
        accessibleBy {
          relation
          organizationUser {
            id
            firstName
            lastName
            email
          }
        }
      }
    }
  }
`;

const FETCH_FORM_COMPLETIONS = gql`
  query fetchFormCompletions($input: FetchFormCompletionsInput!) {
    fetchFormCompletions(input: $input) {
      formCompletions {
        id
        translatedName {
          en
          fr
        }
        state
        signingType
        requiredSignatures {
          signerKey
        }
      }
      totalCount
    }
  }
`;

export const ManageSigners = ({
  onNext, workflowCompletion,
}: {
  onNext: () => void;
  workflowCompletion: any;
}) => {
  const { t } = useTranslation(['workflowCompletions']);
  const { activeOrganization } = useContext(UserContext);
  const [signerMapping, setSignerMapping] = useState<any[]>([]);
  const { workflowData, setWorkflowData } = useContext(WorkflowContext);

  const { data: formCompletionsData } = useQuery(FETCH_FORM_COMPLETIONS, {
    variables: {
      input: {
        filter: {
          workflowCompletionId: workflowCompletion.id,
          states: ['UNSIGNED', 'SIGNED'],
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    skip: !workflowCompletion?.id,
  });

  const { data } = useQuery(FETCH_SIGNER_TYPES, {
    fetchPolicy: 'no-cache',
    variables: {
      input: {
        filter: {
          organizationId: activeOrganization.id,
        },
        pagination: { perPage: 1000 },
      },
    },
  });

  const { data: userData } = useQuery(FETCH_USER, {
    fetchPolicy: 'no-cache',
    variables: {
      userId: workflowCompletion.objectId,
    },
  });

  useEffect(() => {
    if (formCompletionsData?.fetchFormCompletions?.formCompletions && data?.fetchSignerTypes?.signerTypes && userData?.fetchUser?.user) {
      let signerKeys: string[] = [];
      formCompletionsData.fetchFormCompletions.formCompletions.forEach((formCompletion: any) => {
        signerKeys.push(...formCompletion.requiredSignatures.map((signature: any) => signature.signerKey));
      });
      signerKeys = uniq(signerKeys);
      const currentSignerMapping = workflowCompletion.context.signerMapping || [];
      const owner = userData.fetchUser.user.accessibleBy.find((x: any) => x.relation === 'OWNER');
      const formSigners: (FormSignerMapping | undefined)[] = signerKeys.map((signerKey: string) => {
        let signer = currentSignerMapping.find((s: any) => s.signerKey === signerKey);
        const signerType = data.fetchSignerTypes.signerTypes.find((type: any) => type.key === signerKey);
        if (!signerType) {
          return undefined;
        }
        if (!signer && owner) {
          signer = {
            signerName: signerType.translatedName,
            firstName: owner.organizationUser.firstName,
            lastName: owner.organizationUser.lastName,
            email: owner.organizationUser.email,
            organizationUserId: owner.organizationUser.id,
          };
        }
        return {
          signerKey,
          signerName: signerType.translatedName,
          firstName: signer?.firstName,
          lastName: signer?.lastName,
          email: signer?.email,
          organizationUserId: signer?.organizationUserId,
          useOrgUser: true,
        };
      });
      setSignerMapping(formSigners.filter((s: FormSignerMapping | undefined) => s !== undefined));
    }
  }, [workflowCompletion, data, userData, formCompletionsData]);

  const save = () => {
    setWorkflowData({
      ...workflowData,
      signerMapping,
    });
    onNext();
  };

  return (
    <Box display='flex' flexDirection='column' gap={2}>
      <Typography variant='displayLarge' sx={{ mt: 1, mb: 2 }}>
        {t('manageSigners.manageSigners')}
      </Typography>
      {
        signerMapping.map((signer: FormSignerMapping) => (
          <Box key={signer.signerKey} display='flex' alignItems='center' gap={1} width='100%'>
            <Card variant='outlined' sx={{ width: '100%' }}>
              <CardContent>
                <Box display='flex' alignItems='center' gap={1} justifyContent='space-between'>
                  <Typography variant='bodyLarge'>
                    {translateBackend(signer.signerName)}
                  </Typography>
                  <Switch checked={signer.useOrgUser} label={t('manageSigners.useOrgUser')} onChange={(e: any) => setSignerMapping(signerMapping.map((s: FormSignerMapping) => (
                    s.signerKey === signer.signerKey ? { ...s, useOrgUser: e } : s
                  )))} />
                </Box>
                { signer.useOrgUser ? (
                  <Box mt={2}>
                    <OrganizationUserSelect value={signer.organizationUserId ?? ''} onChange={(value: any) => setSignerMapping(signerMapping.map((s: FormSignerMapping) => (
                      s.signerKey === signer.signerKey ? {
                        ...s,
                        organizationUserId: value?.id,
                        firstName: value?.firstName,
                        lastName: value?.lastName,
                        email: value?.email,
                      } : s
                    )))} />
                  </Box>
                ) : (
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={4}>
                      <TextField label={t('manageSigners.firstName')} value={signer.firstName} onChange={(e: any) => setSignerMapping(signerMapping.map((s: FormSignerMapping) => (
                        s.signerKey === signer.signerKey ? { ...s, firstName: e.target.value } : s
                      )))} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label={t('manageSigners.lastName')} value={signer.lastName} onChange={(e: any) => setSignerMapping(signerMapping.map((s: FormSignerMapping) => (
                        s.signerKey === signer.signerKey ? { ...s, lastName: e.target.value } : s
                      )))} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label={t('manageSigners.email')} value={signer.email} onChange={(e: any) => setSignerMapping(signerMapping.map((s: FormSignerMapping) => (
                        s.signerKey === signer.signerKey ? { ...s, email: e.target.value } : s
                      )))} />
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
          </Box>
        ))
      }
      <Box display='flex' justifyContent='end'>
        <Button
          dataTestId={'manage-signers-continue-button'}
          label={t('continue')}
          sx={{ mt: 3, textAlign: 'center' }}
          onClick={() => save()}
        />
      </Box>
    </Box>
  );
};
